import { useNavigate } from 'react-router-dom';
import type { Candidate } from '@/types';

interface CandidatesTableProps {
  candidates: Candidate[];
}

const statusLabels: Record<string, string> = {
  new: 'Новый',
  resume_scored: 'Резюме оценено',
  form_sent: 'Письмо отправлено',
  answers_received: 'Ответы получены',
  answers_scored: 'Анкета оценена',
  interview_scheduled: 'Интервью',
  rejected: 'Отказ',
};

const statusColors: Record<string, string> = {
  new: 'bg-background-100 text-foreground-600',
  resume_scored: 'bg-purple-50 text-purple-700',
  form_sent: 'bg-secondary-100 text-secondary-700',
  answers_received: 'bg-accent-100 text-accent-700',
  answers_scored: 'bg-emerald-50 text-emerald-700',
  interview_scheduled: 'bg-amber-50 text-amber-700',
  rejected: 'bg-red-50 text-red-600',
};

export default function CandidatesTable({ candidates }: CandidatesTableProps) {
  const navigate = useNavigate();

  if (candidates.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-16"
      >
        <div className="w-12 h-12 rounded-full bg-background-100 flex items-center justify-center mb-3"
        >
          <i className="ri-user-search-line text-xl text-foreground-400"></i>
        </div>
        <p className="text-sm text-foreground-500">Нет кандидатов</p>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-xl border border-background-200 overflow-hidden"
    >
      <div className="overflow-x-auto"
      >
        <table className="text-sm"
        >
          <thead>
            <tr className="border-b border-background-200 bg-background-50"
            >
              <th className="text-left px-4 py-3 text-xs font-medium text-foreground-500 w-[190px]"
              >Кандидат</th>
              <th className="text-left px-4 py-3 text-xs font-medium text-foreground-500 w-[120px]"
              >Статус</th>
              <th className="text-left px-4 py-3 text-xs font-medium text-foreground-500 w-[190px]"
              >Контакты</th>
              <th className="text-center px-3 py-3 text-xs font-medium text-foreground-500 w-[80px]"
              >Резюме</th>
              <th className="text-center px-3 py-3 text-xs font-medium text-foreground-500 w-[80px]"
              >Анкета</th>
              <th className="text-center px-3 py-3 text-xs font-medium text-foreground-500 w-[90px]"
              >Совместим.</th>
              <th className="text-center px-3 py-3 text-xs font-medium text-foreground-500 w-[70px]"
              >Итог</th>
            </tr>
          </thead>
          <tbody>
            {candidates.map((c) => (
              <tr
                key={c.id}
                onClick={() => navigate(`/candidates/${c.id}`)}
                className={`border-b border-background-100 last:border-0 hover:bg-background-50 transition-colors cursor-pointer ${c.hr_status === 'declined' ? 'opacity-50' : ''}`}
              >
                <td className="px-4 py-3"
                >
                  <div className="flex items-center gap-3"
                  >
                    <div className="w-8 h-8 rounded-full bg-primary-100 flex items-center justify-center shrink-0"
                    >
                      <span className="text-primary-700 font-heading font-semibold text-xs"
                      >
                        {c.name.charAt(0).toUpperCase()}
                      </span>
                    </div>
                    <div className="min-w-0 flex-1"
                    >
                      <p className="font-medium text-foreground-900 truncate flex items-center gap-1.5"
                      >
                        {!c.viewed_at && <span title="Не просмотрен" className="w-1.5 h-1.5 rounded-full bg-primary-500 shrink-0 inline-block"></span>}
                        {c.name}
                        {c.hr_status === 'meeting' && (
                          <span className="text-[10px] font-bold px-1.5 py-0.5 rounded-full bg-amber-50 text-amber-700 shrink-0">
                            Встреча {c.meeting_at ? new Date(c.meeting_at).toLocaleString('ru-RU', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' }) : ''}
                          </span>
                        )}
                        {c.hr_status === 'declined' && (
                          <span className="text-[10px] font-bold px-1.5 py-0.5 rounded-full bg-red-50 text-red-600 shrink-0">Отказ</span>
                        )}
                      </p>
                      {c.position && (
                        <p className="text-xs text-foreground-400 break-words leading-snug"
                        >{c.position}</p>
                      )}
                      {c.hr_note && (
                        <p className="text-xs text-foreground-500 italic break-words">
                          <i className="ri-chat-quote-line mr-1 text-foreground-300"></i>
                          {c.hr_note}
                        </p>
                      )}
                    </div>
                  </div>
                </td>
                <td className="px-4 py-3"
                >
                  <span className={`inline-flex text-[11px] font-medium px-2 py-0.5 rounded-full ${statusColors[c.status] || statusColors.new}`}
                  >
                    {statusLabels[c.status] || c.status}
                  </span>
                  {c.replied && (
                    <span className="ml-1.5 inline-flex w-2 h-2 rounded-full bg-accent-500"
                    ></span>
                  )}
                </td>
                <td className="px-4 py-3"
                >
                  <div className="flex flex-col gap-1 w-[170px]"
                  >
                    {c.email && (
                      <span className="text-[11px] text-foreground-500 bg-background-100 px-1.5 py-0.5 rounded truncate whitespace-nowrap max-w-full"
                      >
                        <i className="ri-mail-line mr-0.5"></i>
                        {c.email}
                      </span>
                    )}
                    {c.phone && (
                      <span className="text-[11px] text-foreground-500 bg-background-100 px-1.5 py-0.5 rounded truncate whitespace-nowrap max-w-full"
                      >
                        <i className="ri-phone-line mr-0.5"></i>
                        {c.phone}
                      </span>
                    )}
                    {c.telegram && (
                      <span className="text-[11px] text-foreground-500 bg-background-100 px-1.5 py-0.5 rounded truncate whitespace-nowrap max-w-full"
                      >
                        <i className="ri-telegram-line mr-0.5"></i>
                        @{c.telegram}
                      </span>
                    )}
                    {c.whatsapp && (
                      <span className="text-[11px] text-foreground-500 bg-background-100 px-1.5 py-0.5 rounded truncate whitespace-nowrap max-w-full"
                      >
                        <i className="ri-whatsapp-line mr-0.5"></i>
                        {c.whatsapp}
                      </span>
                    )}
                  </div>
                </td>
                <td className="px-3 py-3 text-center"
                >
                  <span className="inline-flex items-center gap-1 text-sm font-heading font-bold text-purple-700 bg-purple-50 px-2 py-1 rounded-lg"
                  >
                    {c.resume_score || 0}
                    <span className="text-[10px] font-normal text-purple-400">/100</span>
                  </span>
                </td>
                <td className="px-3 py-3 text-center"
                >
                  <span className="inline-flex items-center gap-1 text-sm font-heading font-bold text-emerald-700 bg-emerald-50 px-2 py-1 rounded-lg"
                  >
                    {c.answers_score || 0}
                    <span className="text-[10px] font-normal text-emerald-400">/100</span>
                  </span>
                </td>
                <td className="px-3 py-3 text-center"
                >
                  <span className="inline-flex items-center gap-1 text-sm font-heading font-bold text-amber-700 bg-amber-50 px-2 py-1 rounded-lg"
                  >
                    {c.compatibility_score || 0}
                    <span className="text-[10px] font-normal text-amber-400">/100</span>
                  </span>
                </td>
                <td className="px-3 py-3 text-center"
                >
                  <span className="text-lg font-heading font-bold text-foreground-900"
                  >{c.total_score || 0}</span>
                  <span className="text-[10px] text-foreground-400 block"
                  >/300</span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}